// Состояние интерфейса (не партии): экран, кто держит телефон, выбор в запросах, таймеры.
import type { CardId, CardRef, Iid, MutatorId, PlayerId, TraitId, BotLevel, StarId, TableLevel } from '../types.ts';
import { normalizeMutators, PICKABLE_MUTATORS } from '../engine/mutators.ts';
import type { ZoomKind } from './overlays.ts';

export type Screen = 'start' | 'setup' | 'game';
/** Главный экран: хаб с режимами или экран режима (онлайн-комнаты / своя партия на одном телефоне) */
export type HomeTab = 'hub' | 'bots' | 'online' | 'hotseat' | 'top' | 'daily' | 'duel' | 'corr';
/** Номинация на экране рейтингов: против людей или против ботов уровня */
/** Экран рейтингов: «all» — витрина (кто чем выделяется, звёзды, номинации), иначе — таблица номинации или звезды */
export type TopTab = 'all' | 'people' | 'daily' | TableLevel | StarId;
/** play — стол активного игрока; buy — барахолка; mine — мой стол (в чужой ход) */
export type Mode = 'play' | 'buy' | 'mine';
export type SetupStage = 'pass' | 'choose';
export type NetStatus = 'connected' | 'disconnected' | '';

export interface SetupUi {
  pid: PlayerId;
  traitId: TraitId | null;
  familiar: CardId | null;
  /** третий (ничейный) фамильяр во второй фазе подготовки */
  freeFam: CardId | null;
  stage: SetupStage;
  online: boolean;
}

export interface PendingTarget {
  iid: Iid;
  pid: PlayerId;
}

/** Открытая крупно карта: чтобы перерисовать окно из состояния (степпер чипсин), а не править DOM руками */
export interface ZoomState {
  card: CardRef;
  kind: ZoomKind;
  useChips: number;
}

export type Timer = ReturnType<typeof setTimeout>;

export interface UiState {
  /** кто сейчас держит телефон (хотсит) / мой pid (онлайн) */
  viewer: PlayerId | null;
  screen: Screen;
  home: HomeTab;
  setup: SetupUi;
  names: string[];
  /** кто из мест — бот и какого уровня (null — человек) */
  bots: (BotLevel | null)[];
  nPlayers: number;
  /** «Против ботов»: сколько соперников и какого уровня (запоминается) */
  quickBots: number;
  quickLevel: BotLevel;
  /** «Против ботов»: один из ботов — босс (случайный) */
  quickBoss: boolean;
  /** игра по переписке: сколько мест в новой партии */
  corrSize: number;
  /** игра по переписке: набор, куда позвали ссылкой (?corr=КОД) — в списке открытых первым и выделен */
  corrFocus: string;
  /** мутаторы новой партии («Против ботов», «Своя партия»); запоминаются */
  mutators: MutatorId[];
  /** экран рейтингов: открытая номинация */
  topTab: TopTab;
  /** хотсит: боты ходят мгновенно, перед твоим ходом — сводка (выкл — по шагам с анимациями, как раньше) */
  instantBots: boolean;
  /** хотсит: режим ребаланса — правленые цены самых несбалансированных карт (src/engine/rebalance.ts) */
  rebalance: boolean;
  /** имя игрока для онлайна и списка «кто онлайн» (хранится в localStorage) */
  onlineName: string;
  joinCode: string;
  /** выбранные карты в запросе chooseCards */
  selected: Set<Iid>;
  numVal: number | null;
  zoom: ZoomState | null;
  /** в карточке показан скан оригинала (кнопка «Оригинал») */
  zoomOrig: boolean;
  /** что показать снова, когда закроется карточка, открытая поверх другого окна (журнала) */
  zoomBack: (() => void) | null;
  netStatus: NetStatus;
  mode: Mode;
  /** iid поднятой карты руки */
  raised: Iid | null;
  /** рука развёрнута в режиме наблюдения */
  handOpen: boolean;
  /** лист запроса свёрнут (чтобы взглянуть на стол) */
  sheetCollapsed: boolean;
  /** последнее анимированное событие */
  lastSeq: number;
  /** хотсит: до какого события каждый игрок видел происходящее */
  seenSeq: Record<number, number>;
  /** цель, выбранная перетаскиванием карты на игрока */
  pendingTarget: PendingTarget | null;
  botTimer: Timer | null;
  autoEndTimer: Timer | null;
  autoEndAt: number | null;
  /** seq события «ход» текущего хода — для автопереключения вкладок раз за ход */
  turnSeq: number | null;
  autoBuyDone: boolean;
  endShown: boolean;
  /** на экране — плеер сыгранной партии (профиль → поражение): стол только для просмотра, см. replay.ts */
  replay: boolean;
}

/** Состояние UI на игровом экране: зритель известен */
export type GameUi = UiState & { viewer: PlayerId };

export function freshSetup(pid: PlayerId, stage: SetupStage, online = false): SetupUi {
  return { pid, traitId: null, familiar: null, freeFam: null, stage, online };
}

function savedQuickLevel(): BotLevel {
  try {
    const l = localStorage.getItem('krutagidon-quick-level');
    return l === 'easy' || l === 'normal' || l === 'hard' || l === 'cheat' ? l : 'normal';
  } catch {
    return 'normal';
  }
}

export function initialUi(): UiState {
  return {
    viewer: null,
    screen: 'start',
    home: 'hub',
    setup: freshSetup(0, 'pass'),
    names: ['', '', '', '', ''],
    bots: [null, null, null, null, null],
    nPlayers: 3,
    instantBots: (() => { try { return localStorage.getItem('krutagidon-instant-bots') !== '0'; } catch { return true; } })(),
    quickBots: (() => { try { const n = Number(localStorage.getItem('krutagidon-quick-n')); return n >= 1 && n <= MAX_PLAYERS - 1 ? n : 2; } catch { return 2; } })(),
    quickLevel: savedQuickLevel(),
    quickBoss: (() => { try { return localStorage.getItem('krutagidon-quick-boss') === '1'; } catch { return false; } })(),
    corrSize: 3,
    corrFocus: '',
    mutators: (() => { try { const v: unknown = JSON.parse(localStorage.getItem('krutagidon-mutators') ?? '[]'); return normalizeMutators(Array.isArray(v) ? v : []).filter((m) => PICKABLE_MUTATORS.includes(m)); } catch { return []; } })(),
    // рейтинги открываются на уровне ботов, с которыми игрок обычно играет
    topTab: 'all',
    rebalance: (() => { try { return localStorage.getItem('krutagidon-rebalance') === '1'; } catch { return false; } })(),
    // имя запоминается: им же клиент представляется в списке «кто онлайн»
    onlineName: (() => { try { return localStorage.getItem('krutagidon-name') ?? ''; } catch { return ''; } })(),
    joinCode: '',
    selected: new Set(),
    numVal: null,
    zoom: null,
    zoomOrig: false,
    zoomBack: null,
    netStatus: '',
    mode: 'play',
    raised: null,
    handOpen: false,
    sheetCollapsed: false,
    lastSeq: 0,
    seenSeq: {},
    pendingTarget: null,
    botTimer: null,
    autoEndTimer: null,
    autoEndAt: null,
    turnSeq: null,
    autoBuyDone: false,
    endShown: false,
    replay: false,
  };
}

export const MAX_PLAYERS = 5;
export const MIN_PLAYERS = 2;
